<template>
  <div
    v-if="title && link"
    :class="{ 'has-children': hasChildren }"
    @closeSubmenu="onCloseSubmenu"
    @mouseenter="hasChildren && openMenu()"
    @mouseleave="hasChildren && closeMenu()"
  >
    <NuxtLink
      :aria-label="title + (isStoryLink ? '' : ' - opens in new tab')"
      :class="[anchorClass]"
      :external="!isStoryLink"
      :target="isStoryLink ? '' : '_blank'"
      :to="{ path: link }"
      @click="toggleMenu"
    >
      <i
        v-if="hasIcon && isIconComponent"
        aria-hidden
        class="icon icon--illustrative"
      >
        <Icon :name="icon" />
      </i>
      <span>
        {{ title }}
      </span>
      <i v-if="hasChildren" class="icon icon--opens">
        <Icon name="fa6-solid:chevron-down" />
      </i>
    </NuxtLink>
    <Transition
      name="nav"
      @after-enter="transitionAfterEnter"
      @after-leave="transitionAfterLeave"
      @before-enter="transitionBeforeEnter"
      @before-leave="transitionBeforeLeave"
      @enter="transitionEnter"
      @leave="transitionLeave"
    >
      <!-- child nav -->
      <NavigationMenuChild
        v-show="hasChildren && isActive"
        :blok="blok.children"
      />
    </Transition>
    <!-- clickaway -->
    <div
      v-if="hasChildren && isActive"
      class="nav-trigger"
      @mouseover="closeMenu"
    />
  </div>
</template>

<script setup lang="ts">
import type { NavigationMenuItemStoryblok } from '~/types/storyblok'
const props = defineProps<{
  blok: NavigationMenuItemStoryblok
  anchorClass?: string
  index?: number
}>()
const emit = defineEmits(['onCloseNav', 'closeSubmenu'])
const route = useRoute()
const icon = computed(() => {
  switch (props.blok.icon) {
    case 'fas fa-shield-alt':
      return 'fa6-solid:shield'
    case 'fas fa-globe-americas':
      return 'fa6-solid:earth-americas'
    case 'fab fa-facebook-square':
      return 'fa6-brands:square-facebook'
    case 'fab fa-youtube':
      return 'fa6-brands:youtube'
    default:
      return ''
  }
})
const isActive = ref(false)
const hasIcon = computed(() => !!props.blok.icon)
const hasChildren = computed(() => !!props.blok.children?.length)
const isIconComponent = computed(() => !!icon.value)
const isStoryLink = computed(() => props.blok.link?.linktype === 'story')
const link = computed(() => {
  return props.blok.link?.cached_url === '/'
    ? '/'
    : isStoryLink.value
      ? '/' + props.blok.link?.cached_url
      : props.blok.link?.url
})
const title = computed(() => props.blok.label || props.blok.link?.story?.name)
const shouldNavigateToLink = computed(
  () => !hasChildren.value || isActive.value,
)
const isCurrentPage = computed(() => link.value === route.fullPath)

watch(
  () => route.path,
  () => {
    isActive.value = false
  },
)

function openMenu() {
  isActive.value = true
}
function closeMenu() {
  isActive.value = false
}
function toggleMenu() {
  if (!shouldNavigateToLink.value) {
    emit('closeSubmenu', props.index)
    isActive.value = !isActive.value
  } else if (isCurrentPage.value && shouldNavigateToLink) {
    emit('onCloseNav')
    // close open item if it's the same page
    closeMenu()
  }
}

function onCloseSubmenu(index: number) {
  if (index !== props.index) closeMenu()
}

function transitionBeforeEnter(el: HTMLElement) {
  el.style.height = '0'
}

function transitionAfterEnter(el: HTMLElement) {
  el.style.height = ''
}

function transitionEnter(el: HTMLElement) {
  el.style.height = el.scrollHeight + 'px'
  el.style.opacity = '1'
}

function transitionBeforeLeave(el: HTMLElement) {
  el.style.minHeight = el.scrollHeight + 'px'
  el.style.maxHeight = el.scrollHeight + 'px'
}

function transitionLeave(el: HTMLElement) {
  el.style.minHeight = '0'
  el.style.maxHeight = '0'
  el.style.height = '0'
  el.style.opacity = '0'
}

function transitionAfterLeave(el) {
  el.style.minHeight = ''
  el.style.maxHeight = ''
}
</script>
